import { MediaAsset, MediaAssetType } from 'types';
import type { RoleplayPersona } from 'services/api/roleplayClient';
import type { FactoryCourseImpact } from 'services/api/adminContentClient';

export type AdminQuestion = {
  id?: number;
  key: string;
  text: string;
  header?: string | null;
  subtext?: string | null;
  category: string;
  question_type?: { id: number; name: string };
  question_type_id?: number;
  low_text?: string | null;
  high_text?: string | null;
  metadata?: Record<string, unknown>;
  citations?: string[];
  options?: QuestionOptionEditable[];
  is_active?: boolean;
  media?: MediaAsset | null;
  media_asset_id?: string | null;
  flows?: Array<{
    flow_id: number;
    flow_name: string;
    order: number;
    is_required: boolean;
  }>;
  radar_memberships?: Array<{
    definition_key: string;
    definition_name: string;
    axis_key: string;
    axis_label: string;
  }>;
  affected_factory_courses?: FactoryCourseImpact[];
};

export type QuestionOptionEditable = {
  id?: number;
  front_text: string;
  back_text: string;
  value: number;
  is_correct: boolean;
  is_other: boolean;
  order?: number;
};

export type QuestionTypeOption = {
  id: number;
  name: string;
};

export type CourseOption = {
  id: number;
  name: string;
};

export type FlowOption = {
  id: number;
  name: string;
};

export type AdminRoleplayPersona = RoleplayPersona & { id: number };

export type DiffDetail = {
  field: string;
  before: unknown;
  after: unknown;
};

/** An earlier-answer reference learners in one of the question's modules could never see answered. */
export type AnswerReferenceWarning = {
  code: string;
  message: string;
  flow_name: string;
};

export type MultipleSelectLimitInputs = {
  minSelections: string;
  maxSelections: string;
};

export type MultipleSelectLimitValidation = {
  minError?: string;
  maxError?: string;
};

export const QUESTION_TYPE_OPTIONS = [
  { label: 'Callback', value: 'callback_summary' },
  { label: 'Drag and Drop Horizontal', value: 'horizontal_drag_drop' },
  { label: 'Drag and Drop Ranking', value: 'drag_and_drop_ranking' },
  { label: 'Image', value: 'image' },
  { label: 'Multiple Choice', value: 'multiple_choice' },
  { label: 'Multiple Select', value: 'multiple_select' },
  { label: 'Rating', value: 'rating' },
  { label: 'Tap to Reveal', value: 'multiple_reveal' },
  { label: 'Accordion', value: 'accordion' },
  { label: 'Additional Resources', value: 'additional_resources' },
  { label: 'Text/Audio', value: 'text_or_audio' },
  { label: 'Video', value: 'video_player' },
  { label: 'PDF', value: 'pdf' },
  { label: 'Boolean / True/False', value: 'boolean' },
  { label: 'List', value: 'text_list' },
  { label: 'Description', value: 'description' },
  { label: 'AI Summary', value: 'ai_summary' },
  { label: 'Roleplay', value: 'roleplay' },
];

export const QUESTION_TYPE_LABEL_BY_SLUG: Record<string, string> = QUESTION_TYPE_OPTIONS.reduce(
  (acc, option) => {
    acc[option.value] = option.label;
    return acc;
  },
  {} as Record<string, string>,
);

export const QUESTION_TYPE_MEDIA_RESTRICTIONS: Partial<Record<string, MediaAssetType[]>> = {
  image: ['image'],
  video_player: ['video'],
  pdf: ['pdf'],
  text_or_audio: ['audio'],
};

export const OPTION_REQUIRED_TYPES = new Set([
  'multiple_choice',
  'multiple_select',
  'multiple_reveal',
  'accordion',
  'additional_resources',
  'horizontal_drag_drop',
  'drag_and_drop_ranking',
  'drag_and_drop',
]);

export const FIELD_LABELS: Record<string, string> = {
  text: 'Question Text',
  header: 'Header',
  subtext: 'Subtext',
  category: 'Category',
  question_type_id: 'Question Type',
  is_active: 'Active Status',
  low_text: 'Low Text',
  high_text: 'High Text',
  citations: 'Citations',
  metadata: 'Metadata',
  media_asset_id: 'Media Asset',
  options: 'Options',
};

export const DEFAULT_QUESTION_TYPE = QUESTION_TYPE_OPTIONS[0]?.value ?? 'multiple_choice';

export const EMPTY_QUESTION: AdminQuestion = {
  key: '',
  text: '',
  category: 'general',
  question_type_id: undefined,
  header: '',
  subtext: '',
  low_text: '',
  high_text: '',
  metadata: {},
  citations: [],
  media: null,
  media_asset_id: null,
};
